import { ChangeEvent, useState } from "react";
import { useAddChildCollectionMutation, useCreateCollectionMutation } from "@/api/api";
import { Plus } from "lucide-react";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog";
import { Input } from "../input";
import { Checkbox } from "../checkbox";
import { Field, FieldLabel } from "../field";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { SidebarGroupAction } from "../sidebar";
import { useCollection, useIsOwnSpace } from "@/api/hooks";

export default function CollectionCreate() {
  const [open, setOpen] = useState(false);
  const me = useAppSelector(selectMe);
  const isOwnSpace = useIsOwnSpace();
  const activeCollection = useCollection();

  const [createCollection] = useCreateCollectionMutation();
  const [addChildCollection] = useAddChildCollectionMutation();
  const [title, setTitle] = useState<string>("");
  const [asChild, setAsChild] = useState(true);

  if (!me || !isOwnSpace) return null;

  const handleCreate = async () => {
    if (title.trim().length === 0) return;
    try {
      const { id } = await createCollection({ title }).unwrap();
      if (asChild && activeCollection) {
        await addChildCollection({
          parentID: activeCollection.id,
          childIDs: [id],
        });
      }
    } catch {
      // No error UI here -- the dialog closes either way; a failed create
      // just means nothing new shows up.
    }
    setTitle("");
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setTitle("");
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <SidebarGroupAction>
          <Plus />
        </SidebarGroupAction>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
          <DialogHeader>
            <DialogTitle>New collection</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Field>
              <FieldLabel htmlFor="collectionTitle">Name</FieldLabel>
              <Input
                id="collectionTitle"
                value={title}
                placeholder="Enter collection name"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setTitle(e.target.value)
                }
                autoFocus
              />
            </Field>
            {activeCollection && (
              <Field orientation="horizontal" className="mt-3">
                <Checkbox
                  id="collectionAsChild"
                  checked={asChild}
                  onCheckedChange={(checked) => setAsChild(checked === true)}
                />
                <FieldLabel htmlFor="collectionAsChild" className="font-normal">
                  Make child of &ldquo;{activeCollection.title}&rdquo;
                </FieldLabel>
              </Field>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={title.trim().length === 0}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
